import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Copy, Settings2, FileText, Pencil, Info, ChevronDown, ChevronUp, Trash2, FlaskConical } from 'lucide-react';
import { Separator } from '../components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function WebhookConfigPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formType, setFormType] = useState('other');
  const [creating, setCreating] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editType, setEditType] = useState('other');
  const [editSaving, setEditSaving] = useState(false);
  const [guideOpen, setGuideOpen] = useState(true);
  const [formTypes, setFormTypes] = useState([]);
  const [deleteForm, setDeleteForm] = useState(null);
  const [deletingForm, setDeletingForm] = useState(false);

  const isAdmin = user?.role === 'admin';

  const fetchForms = () => {
    api.get('/forms').then(r => setForms(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchForms(); }, []);

  useEffect(() => {
    api.get('/form-types').then(r => {
      const types = r.data;
      setFormTypes(Array.isArray(types) ? types : []);
    }).catch(() => {});
  }, []);

  const handleCreate = async () => {
    if (!formName.trim()) { toast.error('Form name is required'); return; }
    setCreating(true);
    try {
      await api.post('/forms', { name: formName, description: formDesc, form_type: formType });
      toast.success('Form registered');
      setShowDialog(false);
      setFormName('');
      setFormDesc('');
      setFormType('other');
      fetchForms();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create form');
    } finally {
      setCreating(false);
    }
  };

  const copyUrl = (slug) => {
    navigator.clipboard.writeText(`${BACKEND_URL}/api/webhook/${slug}`);
    toast.success('Webhook URL copied');
  };

  const toggleStatus = async (form) => {
    const newStatus = form.status === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/forms/${form.id}`, { status: newStatus });
      toast.success(`Form ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchForms();
    } catch {
      toast.error('Failed to update');
    }
  };

  const openEditForm = (form) => {
    setEditForm(form);
    setEditName(form.name);
    setEditDesc(form.description || '');
    setEditType(form.form_type || 'other');
    setShowEditDialog(true);
  };

  const handleEditSave = async () => {
    if (!editName.trim()) { toast.error('Form name is required'); return; }
    setEditSaving(true);
    try {
      await api.put(`/forms/${editForm.id}`, { name: editName, description: editDesc, form_type: editType });
      toast.success('Form updated');
      setShowEditDialog(false);
      setEditForm(null);
      fetchForms();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteForm = async () => {
    if (!deleteForm) return;
    setDeletingForm(true);
    try {
      await api.delete(`/forms/${deleteForm.id}/permanent`);
      toast.success(`Form "${deleteForm.name}" permanently deleted`);
      setDeleteForm(null);
      fetchForms();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeletingForm(false);
    }
  };

  const toggleTestMode = async (form) => {
    const newMode = !form.test_mode;
    try {
      await api.put(`/forms/${form.id}`, { test_mode: newMode });
      toast.success(newMode ? 'Test Mode ON — new submissions will be tagged as test' : 'Test Mode OFF — new submissions will be treated as real');
      fetchForms();
    } catch {
      toast.error('Failed to update test mode');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><p className="font-data text-sm text-muted-foreground">Loading...</p></div>;

  return (
    <div data-testid="webhook-config-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Webhook Configuration</h1>
          <p className="text-sm text-muted-foreground mt-1">Register Zoho Forms and manage webhook endpoints</p>
        </div>
        {isAdmin && (
          <Button data-testid="register-form-btn" className="rounded-sm" onClick={() => setShowDialog(true)}>
            <Plus className="w-4 h-4 mr-1" /> Register Form
          </Button>
        )}
      </div>

      {/* Setup Guide */}
      <Collapsible open={guideOpen} onOpenChange={setGuideOpen} className="mb-6">
        <Card className="border border-blue-200 bg-blue-50/50 rounded-sm shadow-sm">
          <CollapsibleTrigger asChild>
            <button className="w-full flex items-center justify-between p-5 text-left" data-testid="setup-guide-toggle">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-sm bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Info className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-heading text-sm font-semibold text-blue-900">How to Set Up Webhooks with Zoho Forms</h3>
                  <p className="text-xs text-blue-700 mt-0.5">Step-by-step guide to connect your Zoho Forms</p>
                </div>
              </div>
              {guideOpen ? <ChevronUp className="w-4 h-4 text-blue-600" /> : <ChevronDown className="w-4 h-4 text-blue-600" />}
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="px-5 pb-5">
              <Separator className="mb-4 bg-blue-200" />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
                    <h4 className="text-sm font-semibold text-blue-900">Register Form</h4>
                  </div>
                  <p className="text-xs text-blue-800 leading-relaxed pl-8">Click <strong>"Register Form"</strong> above. Enter the exact name of your Zoho form, select the type (expense, travel, sales, etc.), and add a description. You can rename the form later using the edit button.</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
                    <h4 className="text-sm font-semibold text-blue-900">Copy Webhook URL</h4>
                  </div>
                  <p className="text-xs text-blue-800 leading-relaxed pl-8">Each registered form gets a unique webhook URL. Click the <strong>copy icon</strong> next to the URL. This is the endpoint that Zoho will send data to.</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
                    <h4 className="text-sm font-semibold text-blue-900">Configure in Zoho</h4>
                  </div>
                  <p className="text-xs text-blue-800 leading-relaxed pl-8">In your Zoho Form, go to <strong>Settings &rarr; Integrations &rarr; Webhooks</strong>. Paste the copied URL. Set the method to <strong>POST</strong> and format to <strong>JSON</strong>. Save and submit a test entry.</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">4</span>
                    <h4 className="text-sm font-semibold text-blue-900">Map Fields</h4>
                  </div>
                  <p className="text-xs text-blue-800 leading-relaxed pl-8">After the first submission arrives, the system detects source fields automatically. Click <strong>"Configure Mapping"</strong> to map Zoho fields to internal fields (e.g. map "Amount" &rarr; amount as currency).</p>
                </div>
              </div>
              <Separator className="my-4 bg-blue-200" />
              <div className="flex items-start gap-2 pl-0">
                <p className="text-[11px] text-blue-700 leading-relaxed"><strong>Tip:</strong> The webhook URL stays the same even if you rename the form. After mapping fields, all future submissions will be automatically structured and available for review and dashboards.</p>
              </div>
            </div>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {forms.length === 0 ? (
        <Card className="border border-border rounded-sm shadow-sm">
          <CardContent className="py-16 text-center">
            <p className="text-muted-foreground mb-2">No forms registered yet</p>
            {isAdmin && <p className="text-sm text-muted-foreground">Click "Register Form" to add your first Zoho form</p>}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {forms.map(form => (
            <Card key={form.id} data-testid={`form-card-${form.id}`} className="border border-border rounded-sm shadow-sm hover:shadow-md transition-shadow duration-200">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading text-lg font-semibold">{form.name}</h3>
                      <Badge variant={form.status === 'active' ? 'default' : 'secondary'} className={`text-[10px] rounded-sm ${form.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{form.status}</Badge>
                      {form.test_mode && (
                        <Badge className="text-[10px] rounded-sm bg-amber-100 text-amber-700 border border-amber-300">
                          <FlaskConical className="w-2.5 h-2.5 mr-1" /> TEST MODE
                        </Badge>
                      )}
                    </div>
                    {form.description && <p className="text-sm text-muted-foreground mt-1">{form.description}</p>}
                  </div>
                  <Badge variant="outline" className="text-[10px] rounded-sm">{form.form_type}</Badge>
                </div>

                <div className="flex items-center gap-2 bg-slate-50 border border-border rounded-sm px-3 py-2 mb-3">
                  <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground flex-shrink-0">WEBHOOK</span>
                  <code className="font-data text-xs flex-1 truncate text-foreground">{BACKEND_URL}/api/webhook/{form.slug}</code>
                  <Button variant="ghost" size="sm" className="h-6 px-2 rounded-sm" onClick={() => copyUrl(form.slug)} data-testid={`copy-url-${form.id}`}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>

                <div className="flex items-center gap-6 text-xs text-muted-foreground mb-4">
                  <span className="font-data">{form.submission_count} submissions</span>
                  {form.last_submission_at && <span className="font-data">Last: {formatDistanceToNow(new Date(form.last_submission_at), { addSuffix: true })}</span>}
                  {form.sample_fields?.length > 0 && <span className="font-data">{form.sample_fields.length} detected fields</span>}
                </div>

                <div className="flex items-center gap-2">
                  {isAdmin && (
                    <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => openEditForm(form)} data-testid={`edit-form-${form.id}`}>
                      <Pencil className="w-3 h-3 mr-1" /> Edit
                    </Button>
                  )}
                  {isAdmin && (
                    <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => navigate(`/webhooks/${form.id}/mapping`)} data-testid={`mapping-btn-${form.id}`}>
                      <Settings2 className="w-3 h-3 mr-1" /> Configure Mapping
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="rounded-sm text-xs" onClick={() => navigate(`/submissions?form_id=${form.id}`)}>
                    <FileText className="w-3 h-3 mr-1" /> View Submissions
                  </Button>
                  {isAdmin && (
                    <Button variant="ghost" size="sm" className="rounded-sm text-xs" onClick={() => toggleStatus(form)} data-testid={`toggle-status-${form.id}`}>
                      {form.status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                  )}
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`rounded-sm text-xs ${form.test_mode ? 'text-amber-700 hover:text-amber-800 hover:bg-amber-50' : 'text-muted-foreground hover:bg-slate-50'}`}
                      onClick={() => toggleTestMode(form)}
                      data-testid={`toggle-test-mode-${form.id}`}
                    >
                      <FlaskConical className="w-3 h-3 mr-1" /> {form.test_mode ? 'Disable Test Mode' : 'Test Mode'}
                    </Button>
                  )}
                  {isAdmin && (
                    <Button variant="ghost" size="sm" className="rounded-sm text-xs text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteForm(form)} data-testid={`delete-form-${form.id}`}>
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Register Form Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Register New Form</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Form Name</Label>
              <Input data-testid="form-name-input" value={formName} onChange={e => setFormName(e.target.value)} placeholder="e.g. Travel Expense Form" className="rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Description</Label>
              <Textarea data-testid="form-desc-input" value={formDesc} onChange={e => setFormDesc(e.target.value)} placeholder="Optional description" className="rounded-sm" rows={2} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Form Type</Label>
              <Select value={formType} onValueChange={setFormType}>
                <SelectTrigger className="rounded-sm" data-testid="form-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {formTypes.map(ft => <SelectItem key={ft.id || ft.slug} value={ft.slug}>{ft.name}</SelectItem>)}
                  {formTypes.length === 0 && <SelectItem value="other">Other</SelectItem>}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button data-testid="create-form-btn" className="rounded-sm" onClick={handleCreate} disabled={creating}>
              {creating ? 'Creating...' : 'Register Form'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Form Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Form</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Form Name</Label>
              <Input data-testid="edit-form-name-input" value={editName} onChange={e => setEditName(e.target.value)} className="rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Description</Label>
              <Textarea data-testid="edit-form-desc-input" value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="Optional description" className="rounded-sm" rows={2} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Form Type</Label>
              <Select value={editType} onValueChange={setEditType}>
                <SelectTrigger className="rounded-sm" data-testid="edit-form-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {formTypes.map(ft => <SelectItem key={ft.id || ft.slug} value={ft.slug}>{ft.name}</SelectItem>)}
                  {formTypes.length === 0 && <SelectItem value="other">Other</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            {editForm && (
              <div className="bg-slate-50 border border-border rounded-sm p-3">
                <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Webhook URL (unchanged)</p>
                <code className="font-data text-xs text-foreground">{BACKEND_URL}/api/webhook/{editForm.slug}</code>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button data-testid="save-edit-form-btn" className="rounded-sm" onClick={handleEditSave} disabled={editSaving}>
              {editSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Form Warning */}
      <AlertDialog open={!!deleteForm} onOpenChange={open => { if (!open) setDeleteForm(null); }}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-red-700">Delete Form Permanently</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  You are about to permanently delete <strong>"{deleteForm?.name}"</strong>.
                </p>
                <div className="bg-red-50 border border-red-200 rounded-sm p-3 space-y-2">
                  <p className="font-medium text-red-800">This action will:</p>
                  <ul className="list-disc list-inside text-red-700 text-xs space-y-1">
                    <li>Remove the form and its webhook endpoint</li>
                    <li>Delete all field mappings for this form</li>
                    <li>Permanently delete <strong>{deleteForm?.submission_count || 0} submission(s)</strong> and their reviews</li>
                    <li>The webhook URL <code className="font-data text-[11px]">/api/webhook/{deleteForm?.slug}</code> will stop working immediately</li>
                  </ul>
                </div>
                <p className="text-red-600 font-semibold">This cannot be undone.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-form-btn"
              className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDeleteForm}
              disabled={deletingForm}
            >
              {deletingForm ? 'Deleting...' : 'Delete Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
